import React, { useState } from 'react';

import { Draggable } from '@hello-pangea/dnd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Paper,
  Box,
  Typography,
  Collapse,
  IconButton,
  TextField,
  Stack,
  Chip,
  Button,
  Tooltip,
  Theme,
} from '@mui/material';

import { Effect } from '../../@types/Models';
import { KanbanColumn, KanbanItem, Objective } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';
import { effects } from '../../Effects';

interface Props {
  item: KanbanItem;
  itemInd: number;
  column: KanbanColumn;
  colInd: number;
  theme: Theme;
  isWarPlan: boolean;
  handleCollapse: (item: KanbanItem, isExpanded: boolean) => void;
  handleItemChange: (e: any, colIndex: number, itemInd: number) => void;
  toggleDrawer: () => void;
}

interface Colors {
  [key: string]: string;
}

const colors: Colors = {
  Gn: 'Lime',
  El: 'Orange',
  Ha: 'SteelBlue',
  Or: 'SeaGreen',
  Un: 'Yellow',
  Av: 'DodgerBlue',
  Dw: 'Khaki',
  Dr: 'Green',
  Fa: 'RoyalBlue',
  Da: 'Khaki',
  Hu: 'SteelBlue',
};

const Item: React.FC<Props> = ({
  item,
  itemInd,
  column,
  colInd,
  theme,
  isWarPlan,
  handleCollapse,
  handleItemChange,
  toggleDrawer,
}) => {
  const { intelDispatch } = useIntelState();
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const race = item.province.race ?? '?';

  return (
    <Draggable key={item.id} draggableId={item.id} index={itemInd}>
      {(provided, snapshot) => (
        <Paper
          sx={{
            margin: 0,
            marginTop: 1,
            border: snapshot.isDragging
              ? `1px solid ${theme.palette.primary.main}`
              : 'none',
          }}
          onClick={(e) => handleCollapse(item, true)}
          style={{ ...provided.draggableProps.style }}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              cursor: 'pointer',
            }}
            {...provided.dragHandleProps} // Make the header draggable
            onClick={toggleExpand} // Toggle expand/collapse on click
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexGrow: 1,
              }}
            >
              <Typography variant='body2' noWrap>
                {item.province.slot} {item.province.name}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: colors[race.substring(0, 2)] }}
              >
                {race}
              </Typography>
            </Box>
            <IconButton size='small'>
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>

          {/* Collapsible Content */}
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box sx={{ padding: '8px' }}>
              <Box
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <TextField
                  name={`notes_${itemInd}`}
                  id={`notes_${itemInd}`}
                  variant='outlined'
                  placeholder='add notes to Intel'
                  value={item.content}
                  fullWidth
                  minRows={1}
                  size='small'
                  onChange={(e) => handleItemChange(e, colInd, itemInd)}
                />
              </Box>
              {item.province.requiredOps?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant='caption'
                    style={{ marginTop: '5px' }}
                    textAlign='center'
                    component='div'
                  >
                    Province Ops:
                  </Typography>
                  <Stack
                    direction='row'
                    sx={{
                      maxWidth: '249px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {item.province.requiredOps.map((op, i: number) => {
                      const effect: Effect = effects[op];
                      const isRowEnd = (i + 1) % 4 === 0;
                      return (
                        <Tooltip
                          arrow
                          placement='top'
                          title={`Delete ${op} from Province`}
                          key={effect.displayName}
                        >
                          <Chip
                            label={effect.shortName}
                            variant='outlined'
                            size='small'
                            sx={{
                              marginTop: '5px',
                              marginRight: isRowEnd ? 0 : '3px',
                              width: '60px',
                            }}
                            onDelete={() =>
                              intelDispatch({
                                type: 'DELETE_PROVINCE_OP',
                                payload: { op, province: item.province },
                              })
                            }
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {item.province.requiredObjectives?.length > 0 && (
                <Box>
                  <Typography
                    variant='caption'
                    style={{ marginTop: '5px' }}
                    textAlign='center'
                    component='div'
                  >
                    Province Objectives:
                  </Typography>
                  <Stack
                    direction='row'
                    sx={{
                      maxWidth: '249px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {item.province.requiredObjectives.map((ob: Objective) => (
                      <Tooltip
                        arrow
                        placement='top'
                        title={`Delete the ${ob.type} Objective from Province`}
                        key={ob.type}
                      >
                        <Chip
                          label={`${ob.type}: ${ob.value}`}
                          variant='outlined'
                          size='small'
                          sx={{
                            marginTop: '5px',
                            marginRight: '3px',
                          }}
                          onDelete={() =>
                            intelDispatch({
                              type: 'DELETE_PROVINCE_OBJECTIVE',
                              payload: {
                                objective: ob,
                                province: item.province,
                              },
                            })
                          }
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
              {isWarPlan && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                  }}
                >
                  <Button
                    color='primary'
                    aria-label='add objective'
                    onClick={toggleDrawer}
                    variant='outlined'
                    size='small'
                    sx={{ mt: 1 }}
                  >
                    Add Orders
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}
    </Draggable>
  );
};

export default Item;
